/* eslint-disable functional/immutable-data -- 1フォーム内での計測重複と離脱を管理する */

import { trackEvent } from "./analytics"
import type {
  ContactErrorType,
  ContactPurpose,
  ContactServiceId,
} from "./contact"

const FIELDS = [
  "genre",
  "services",
  "topic",
  "name",
  "email",
  "company",
  "message",
  "consent",
  "email_copy",
  "referenceImage",
]

/** 入力値を受け取らず、許可した項目名と操作の段階だけを記録する。 */
export function createContactFunnel(
  purpose: ContactPurpose,
  service?: ContactServiceId,
) {
  const base = { form_type: purpose, service_id: service ?? "all" }
  let viewed = false
  let startedAt: number | undefined
  let succeeded = false
  let abandoned = false
  let attempted = false
  let lastField = "none"
  const touched = new Set<string>()
  const invalid = new Set<string>()
  const start = (method: "field" | "prompt" | "validation" | "submit") => {
    if (startedAt !== undefined) return
    startedAt = Date.now()
    trackEvent("contact_form_start", { ...base, start_method: method })
  }
  return {
    view(ready: boolean) {
      if (viewed) return
      viewed = true
      trackEvent("contact_form_view", {
        ...base,
        form_ready: ready ? "yes" : "no",
      })
    },
    change(field: string, method: "field" | "prompt" = "field") {
      if (!FIELDS.includes(field)) return
      start(method)
      lastField = field
      invalid.delete(field)
      if (touched.has(field)) return
      touched.add(field)
      trackEvent("contact_field_interaction", { ...base, field_name: field })
    },
    invalid(field: string) {
      if (!FIELDS.includes(field) || invalid.has(field)) return
      start("validation")
      lastField = field
      invalid.add(field)
      trackEvent("contact_form_error", {
        ...base,
        error_type: "validation",
        field_name: field,
      })
    },
    attempt() {
      start("submit")
      attempted = true
      trackEvent("contact_submit_attempt", base)
    },
    error(type: ContactErrorType) {
      trackEvent("contact_form_error", { ...base, error_type: type })
    },
    challengeWait() {
      trackEvent("contact_challenge_wait", base)
    },
    challengeError(
      reason: "script" | "timeout" | "widget" | "unsupported" | "render",
      code?: string,
    ) {
      trackEvent("contact_form_error", {
        ...base,
        error_type: `challenge_${reason}`,
        ...(code && /^\d{6}$/.test(code) ? { challenge_code: code } : {}),
      })
    },
    challengeRecovered() {
      trackEvent("contact_challenge_recovered", base)
    },
    success(serviceCount: number) {
      if (succeeded) return
      succeeded = true
      trackEvent("generate_lead", { ...base, service_count: serviceCount })
    },
    abandon() {
      if (startedAt === undefined || succeeded || abandoned) return
      abandoned = true
      trackEvent("contact_form_abandon", {
        ...base,
        field_name: lastField,
        submit_attempted: attempted ? "yes" : "no",
        elapsed_seconds: Math.min(
          86400,
          Math.max(0, Math.round((Date.now() - startedAt) / 1000)),
        ),
      })
    },
  }
}
